import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TriangleDiagram from "../../../../components/diagrams/TriangleDiagram";
import RootFractionInput, {
  parseRootFractionInput,
} from "../../../../components/inputs/RootFractionInput";
import { formatLatexPreview } from "../../../../components/inputs/RootFractionInput";
import { generateAngleLabel, generateRightTriangleLegs } from "./rightTriangleData";

function gcd(a, b) {
  while (b) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatFraction(numerator, denominator) {
  const d = gcd(numerator, denominator);
  const simpNum = numerator / d;
  const simpDen = denominator / d;

  if (simpDen === 1) return `${simpNum}`;
  return `\\dfrac{${simpNum}}{${simpDen}}`;
}

export function generateTask() {
  let triangle;
  do {
    triangle = generateRightTriangleLegs();
  } while (triangle.a === triangle.b);
  const { a, b, isPythagorean } = triangle;

  const vertex = Math.random() < 0.5 ? "B" : "C";
  const data = {
    a,
    b,
    vertex,
    isPythagorean,
    mirrored: Math.random() < 0.5,
    angleLabel: generateAngleLabel(),
  };
  const numerator = vertex === "B" ? b : a;
  const denominator = vertex === "B" ? a : b;

  return {
    type: "FindTanFromTriangleTask",
    data,
    expectedAnswer: formatFraction(numerator, denominator),
    slope: { numerator, denominator },
  };
}

export function renderTask({ data }) {
  const { a, b, vertex, mirrored, angleLabel = "\\alpha" } = data;
  const angle = vertex === "B" ? "B" : "C";

  return (
    <>
      <p>
        Na podstawie rysunku poniżej, oblicz <InlineMath math={`\\tan${angleLabel}`} />.
        Kąt <InlineMath math={angleLabel} /> znajduje się w wierzchołku{" "}
        <InlineMath math={`${vertex}`} />.
      </p>
      <TriangleDiagram a={a} b={b} angleAt={angle} mirrored={mirrored} alphaLabel={angleLabel} />
    </>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const angleLabel = data?.angleLabel || "\\alpha";
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label={`\\tan${angleLabel}`}
    />
  );
}

export function checkAnswer(task, userAnswer) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  const parsed =
    typeof userAnswer === "object" && userAnswer.parsed != null
      ? userAnswer.parsed
      : parseRootFractionInput(raw);

  const expected = task?.slope?.numerator / task?.slope?.denominator;
  if (parsed == null || expected == null) return false;

  return Math.abs(expected - parsed) < 0.01;
}

export function renderAnswer({ slope, data }) {
  const { numerator, denominator } = slope;
  const angleLabel = data?.angleLabel || "\\alpha";
  return <InlineMath math={`\\tan${angleLabel} = ${formatFraction(numerator, denominator)}`} />;
}

export function renderUserAnswer(userAnswer, task) {
  const raw = typeof userAnswer === "object" ? userAnswer.raw : userAnswer;
  if (!raw || typeof raw !== "string" || raw.trim() === "") return <i>(brak odpowiedzi)</i>;
  const angleLabel = task?.data?.angleLabel || "\\alpha";
  return <InlineMath math={`\\displaystyle ${formatLatexPreview(raw, `\\tan${angleLabel}`)}`} />;
}

export function renderExplanation({ data }) {
  const { a, b, vertex } = data;
  const angleLabel = data?.angleLabel || "\\alpha";
  const numerator = vertex === "B" ? b : a;
  const denominator = vertex === "B" ? a : b;

  const formatted = formatFraction(numerator, denominator);
  const fractionLatex = `\\dfrac{${numerator}}{${denominator}}`;
  const isSimplified = formatted !== fractionLatex;

  return (
    <div className="space-y-4">
      <div>
        <strong>Krok 1:</strong> Korzystamy ze wzoru na tangens kąta ostrego:
        <BlockMath
          math={`\\tan${angleLabel} = \\dfrac{\\text{przyprostokątna~naprzeciw~}${angleLabel}}{\\text{przyprostokątna~przyległa~do~}${angleLabel}}`}
        />
      </div>
      <div>
        <strong>Krok 2:</strong> Wstawiamy dane{isSimplified ? " i upraszczamy ułamek" : ""}:
        <BlockMath math={`\\tan${angleLabel} = ${fractionLatex}`} />
        {isSimplified && <BlockMath math={`\\tan${angleLabel} = ${formatted}`} />}
      </div>
      <div>
        <strong>Krok 3:</strong> Wartość przybliżona:
        <BlockMath math={`\\tan${angleLabel} \\approx ${(numerator / denominator).toFixed(2)}`} />
      </div>
    </div>
  );
}
